/**
 * EdgeContext - Provides edge update functionality to edge components
 *
 * React Flow edge components don't have direct access to update their data.
 * This context passes down the update function from BoardCanvas so edges
 * can modify themselves when users interact with them.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { Edge, Node } from '@xyflow/react';

interface EdgeContextValue {
  /** Update an edge's data */
  updateEdge: (edgeId: string, data: Record<string, unknown>) => void;
  /** Delete an edge */
  deleteEdge: (edgeId: string) => void;
  /** All nodes (for looking up source node widgets in edge popovers) */
  nodes: Node[];
}

const EdgeContext = createContext<EdgeContextValue | null>(null);

interface EdgeProviderProps {
  children: ReactNode;
  /** Callback to update edge data */
  onUpdateEdge: (edgeId: string, data: Record<string, unknown>) => void;
  /** Callback to delete an edge */
  onDeleteEdge: (edgeId: string) => void;
  /** All nodes on the board */
  nodes: Node[];
}

export function EdgeProvider({ children, onUpdateEdge, onDeleteEdge, nodes }: EdgeProviderProps) {
  return (
    <EdgeContext.Provider value={{ updateEdge: onUpdateEdge, deleteEdge: onDeleteEdge, nodes }}>
      {children}
    </EdgeContext.Provider>
  );
}

/**
 * Hook to access edge update functions
 *
 * Use this inside edge components to update their own data.
 */
export function useEdgeContext(): EdgeContextValue {
  const context = useContext(EdgeContext);
  if (!context) {
    // Return no-op functions if not in provider (graceful fallback)
    return {
      updateEdge: () => console.warn('EdgeContext not available'),
      deleteEdge: () => console.warn('EdgeContext not available'),
      nodes: [],
    };
  }
  return context;
}
